"use client"

import { useEffect, useRef, useState } from "react"
import { Slider } from "@/components/ui/slider"
import { Button } from "@/components/ui/button"
import { LayoutGrid } from "@/lib/icons"
import { useResponsiveGridColumns } from "@/hooks/use-responsive-grid-columns"

interface GridColumnsControlProps {
  /** Pass to point this at a different column-count source than the main
   * booru browser's shared setting (e.g. useGoonfolioGridColumns) — omit to
   * use the default global lib/settings-store.ts gridColumns. */
  gridColumns?: number
  setGridColumns?: (value: number) => void
  /** Upper bound for the slider — should track the current breakpoint's ceiling
   * (see useGoonfolioGridColumns) so users can't drag past what fits on screen. */
  maxColumns?: number
}

// Deliberately not a Radix Popover: that portals to <body> and positions via
// the outer window's viewport, which pushed the dropdown off-screen in
// embedded/preview frames. This stays in normal document flow, anchored to
// its own wrapper, so it can never escape the app's own container.
export function GridColumnsControl({ gridColumns: gridColumnsProp, setGridColumns: setGridColumnsProp, maxColumns = 8 }: GridColumnsControlProps = {}) {
  // Only reads the shared global setting when the caller didn't pass its own
  // value/setter — avoids an unused subscription when a caller scopes this
  // control to its own independent grid (e.g. Goonfolio).
  const globalGrid = useResponsiveGridColumns()
  const gridColumns = gridColumnsProp ?? globalGrid.gridColumns
  const setGridColumns = setGridColumnsProp ?? globalGrid.setGridColumns

  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  return (
    <div ref={wrapperRef} className="relative">
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 px-2.5"
        onClick={() => setOpen((v) => !v)}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
        <span className="text-xs tabular-nums">{gridColumns}</span>
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-56 max-w-[min(14rem,calc(100vw-1.5rem))] rounded-md border bg-popover p-4 text-popover-foreground shadow-md">
          <div className="flex items-center gap-3">
            <Slider
              min={1}
              max={maxColumns}
              step={1}
              value={[gridColumns]}
              onValueChange={(v) => setGridColumns(v[0])}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-4 text-right shrink-0">{gridColumns}</span>
          </div>
        </div>
      )}
    </div>
  )
}
